import { Form, Input, message, Modal, Select } from "antd";
import { COUNTRIES } from "common/constants";
import { useState } from "react";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import {
  useAddCourtOfLawMutation,
  useUpdateCourtOfLawMutation,
} from "features/courtsOfLawTable/courtsOfLawAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import styled from "styled-components";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 350px;
`;

export default function AddCourtOfLawModal({ onOk, onCancel, record }) {
  const [form] = Form.useForm();
  const { data: constants } = useFetchBackendConstantsQuery();
  const [addCourtOfLaw] = useAddCourtOfLawMutation();
  const [updateCourtOfLaw] = useUpdateCourtOfLawMutation();

  const [country, setCountry] = useState(record?.country ?? "US");

  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  const onCountryChange = (value) => {
    setCountry(value);
    form.setFieldsValue({ state: undefined, zipCode: undefined });
  };

  const onSubmit = async () => {
    const newCourt = await form.validateFields();
    let result;
    if (record) {
      result = await updateCourtOfLaw({ ...record, ...newCourt, courtId: record.id });
    } else {
      result = await addCourtOfLaw(newCourt);
    }
    if ("data" in result) {
      message.success(`Court of law ${record ? "updated" : "added"} successfully!`, 2);
      onOk();
      form.resetFields();
    }
    if ("error" in result) {
      form.setFields(formatError(result.error));
    }
  };

  return (
    <Modal
      maskClosable={false}
      title="New Court of Law"
      open
      okText="Submit"
      onOk={onSubmit}
      onCancel={onCancel}
    >
      <StyledForm
        form={form}
        layout="vertical"
        initialValues={{ country: "US", ...record }}
        validateMessages={{ required: "This is a required field" }}
      >
        <Form.Item name="code" label="Court Code" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="type" label="Court Type" rules={[{ required: true }]}>
          <Select
            options={constants?.courtTypes.map((courtType) => ({
              label: courtType.display,
              value: courtType.value,
            }))}
          />
        </Form.Item>
        <Form.Item name="name" label="Court Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="department" label="Department">
          <Input />
        </Form.Item>
        <Form.Item name="division" label="Division">
          <Input />
        </Form.Item>
        <Form.Item name="address1" label="Address Line 1">
          <Input />
        </Form.Item>
        <Form.Item name="address2" label="Address Line 2">
          <Input />
        </Form.Item>
        <Form.Item
          name="city"
          label="City"
          rules={[{ pattern: /^[^\d]*$/, message: "Enter a valid city" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="country" label="Country">
          <Select
            placeholder="Select a country..."
            showSearch
            filterOption={filterOption}
            popupMatchSelectWidth={false}
            options={COUNTRIES}
            onChange={onCountryChange}
          />
        </Form.Item>
        <Form.Item name="state" label="State/Province">
          {country === "US" || country === "CA" ? (
            <Select
              filterOption={filterOption}
              showSearch
              popupMatchSelectWidth={false}
              options={(country === "CA" ? constants?.canadianProvinces : constants?.states)?.map(
                (state) => ({
                  label: `${state.display} (${state.value})`,
                  value: state.value,
                }),
              )}
            />
          ) : (
            <Input placeholder="Enter State/Province..." />
          )}
        </Form.Item>
        <Form.Item
          name="zipCode"
          label="Zip/Postal Code"
          dependencies={["country"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const selectedCountry = getFieldValue("country");
                if (selectedCountry !== "US") {
                  return Promise.resolve();
                }
                if (!value || /^\d{5}(-\d{4})?$/.test(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Must be a valid zip code"));
              },
            }),
          ]}
        >
          <Input
            placeholder={country === "CA" ? "A1A 1A1" : "Enter zip code..."}
            maxLength={(() => {
              if (country === "US") return 5;
              if (country === "CA") return 7;
              return undefined;
            })()}
          />
        </Form.Item>
        <Form.Item name="county" label="County">
          <Input />
        </Form.Item>
        <Form.Item name="district" label="District">
          <Input />
        </Form.Item>
        <Form.Item name="phone" label="Phone">
          <Input placeholder="XXX-XXX-XXXX" />
        </Form.Item>
        <Form.Item name="email" label="Email">
          <Input />
        </Form.Item>
        <Form.Item name="url" label="URL">
          <Input />
        </Form.Item>
        <Form.Item name="judgeClerk" label="Judge Clerk">
          <Input />
        </Form.Item>
        <Form.Item name="countyClerk" label="County Clerk">
          <Input />
        </Form.Item>
        <Form.Item name="notes" label="Notes / Description">
          <Input />
        </Form.Item>
      </StyledForm>
    </Modal>
  );
}
